import { apiGet, apiPost, apiPatch, apiDelete } from '../../lib/api/axios';

const toAuthorityEnumName = (authority?: string) => {
  if (!authority) return undefined;
  const trimmed = authority.trim();
  if (!trimmed) return undefined;
  return trimmed.toUpperCase();
};

/**
 * Data ingestion API calls for file upload functionality
 */

// Fetch supported file types for upload
export const getSupportedFileTypes = async () => {
  const response = await apiGet('/admin/files/supported_types', {
    skipTenantPrefix: true,
    meta: { background: true, globalError: false }
  } as any);
  return response.data?.extensions || ['.pdf'];
};

// Fetch containers from data ingestion API
export const getContainers = async () => {
  const response = await apiGet('/admin/files/my-containers', {
    skipTenantPrefix: true,
    meta: { background: true, globalError: false }
  } as any);

  return response.data
    .filter((item: any) => item.type === 'container')
    .map((folder: any) => ({ name: folder.name, access: 'public' as const }));
};

// Upload a file to the data ingestion API
export const uploadFile = async (
  file: File,
  container: string,
  domain?: string,
  subdomain?: string,
  originalUrl?: string,
  jurisdiction?: string,
  authority?: string,
  effectiveFrom?: string,
  effectiveTo?: string
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('target_container', container);

  if (domain) {
    formData.append('domain', domain);
    if (subdomain) {
      formData.append('subdomain', subdomain);
    }
  }

  if (originalUrl) {
    formData.append('original_url', originalUrl);
  }
  if (jurisdiction) {
    formData.append('jurisdiction', jurisdiction);
  }
  if (authority) {
    const authorityEnumName = toAuthorityEnumName(authority);
    if (authorityEnumName) {
      formData.append('authority', authorityEnumName);
    }
  }
  if (effectiveFrom) {
    formData.append('effective_from', effectiveFrom);
  }
  if (effectiveTo) {
    formData.append('effective_to', effectiveTo);
  }

  const response = await apiPost('/admin/files/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    skipTenantPrefix: true,
    meta: {
      title: 'Upload File',
      description: `Uploading ${file.name}...`
    }
  } as any);

  return response.data;
};

// Get file upload status
export const getFileStatus = async (fileId: number) => {
  const response = await apiGet(`/admin/files/${fileId}/status`, {
    skipTenantPrefix: true,
    meta: {
      background: true, // Don't show global error notifications for polling
      globalError: false
    }
  } as any);

  return response.data;
};

// Get all file upload statuses for current user
export const getAllFileStatuses = async () => {
  const response = await apiGet(`/admin/files/status`, {
    skipTenantPrefix: true,
    meta: {
      background: true,
      globalError: false
    }
  } as any);
  return response.data as Array<{
    id: number;
    filename: string;
    user: number;
    uploaded_at: string;
    status: string;
    detail: string;
  }>;
};

/**
 * Update file metadata (domain, subdomain, original_url, summary, publication_date, title)
 * Uses the data ingestion API (PATCH /files/{id}) with JSON body
 */
export const editDocuments = async (
  fileIds: Array<string | number>,
  params: {
    domain?: string;
    subdomain?: string;
    originalUrl?: string;
    summary?: string;
    publicationDate?: string;
    title?: string;
    jurisdiction?: string;
    authority?: string;
    effectiveFrom?: string;
    effectiveTo?: string;
  }
) => {
  const buildPayload = () => {
    const body: Record<string, any> = {};
    if (params.domain !== undefined) body.domain = params.domain;
    if (params.subdomain !== undefined) body.subdomain = params.subdomain;
    if (params.originalUrl !== undefined) body.original_url = params.originalUrl;
    if (params.summary !== undefined) body.summary = params.summary;
    if (params.publicationDate !== undefined) body.publication_date = params.publicationDate;
    if (params.title !== undefined) body.title = params.title;
    if (params.jurisdiction !== undefined) body.jurisdiction = params.jurisdiction;
    if (params.authority !== undefined) body.authority = toAuthorityEnumName(params.authority);
    if (params.effectiveFrom !== undefined) body.effective_from = params.effectiveFrom;
    if (params.effectiveTo !== undefined) body.effective_to = params.effectiveTo;
    return body;
  };

  const payload = buildPayload();
  const requests = fileIds.map((id) =>
    apiPatch(`/admin/files/${encodeURIComponent(id)}`, payload, {
      skipTenantPrefix: true,
      meta: { title: 'Update document', description: `Updating ${id}` }
    } as any)
  );
  await Promise.all(requests);
};

/**
 * Delete one or more files by id.
 * Uses the data ingestion API (DELETE /files/{id})
 */
export const deleteDocuments = async (fileIds: Array<string | number>) => {
  const requests = fileIds.map((id) =>
    apiDelete(`/admin/files/${encodeURIComponent(id)}`, {
      skipTenantPrefix: true,
      meta: { title: 'Delete document', description: `Deleting ${id}` }
    } as any)
  );
  await Promise.all(requests);
};

/**
 * SharePoint integrations API
 */

// Fetch SharePoint connections for current tenant
export const getSharePointConnections = async () => {
  const response = await apiGet('/integrations/sharepoint/my-tenant-connections', {
    meta: { background: true, globalError: false }
  } as any);
  return response.data;
};

// Fetch details for a specific SharePoint connection
export const getSharePointConnectionDetails = async (connectionId: string) => {
  const response = await apiGet(`/integrations/sharepoint/connections/${connectionId}`, {
    meta: { background: true, globalError: false }
  } as any);
  return response.data;
};

// Trigger sync for a specific SharePoint connection
export const syncSharePointConnection = async (connectionId: string) => {
  const response = await apiPost(`/integrations/sharepoint/connections/${connectionId}/sync`, null, {
    meta: { background: true, globalError: false }
  } as any);
  return response.data;
};

// Retry ingestion for a failed SharePoint document
export const retrySharePointDocument = async (connectionId: string, documentId: string) => {
  const response = await apiPost(
    `/integrations/sharepoint/connections/${connectionId}/documents/${documentId}/retry`,
    null,
    {
      meta: { background: false, globalError: true, title: 'Retry document' }
    } as any
  );
  return response.data;
};

/**
 * Scraping integrations API
 */

export interface WikiDocumentInfo {
  id: string;
  title: string;
  title_normalised: string;
  reference: string;
  filestem: string;
  document_type: string;
  status: string;
  visibility: string;
  tenant: string;
  base_document_id: number;
  version_id: number;
  created_at: string;
  updated_at: string;
  short_id: number | null;
  summary: string;
  tags: { category: string; domain: string; subdomain: string };
  public_url: string | null;
  publication_date: string | null;
  issuing_body: string | null;
  jurisdiction: string | null;
  authority: string | null;
  effective_from: string | null;
  effective_to: string | null;
  formats: Array<{
    id: string;
    content_type: string;
    extension: string;
    original: boolean;
    blob_name: string;
    sha256: string;
  }>;
}

export interface ScrapedDocumentChangeEvent {
  url: string;
  scraped_url_id: string | null;
  status_detail: string | null;
  existing_document: WikiDocumentInfo | null;
  new_document: WikiDocumentInfo | null;
  previous_error_count: number | null;
}

export interface ScraperRunResults {
  scraper_run_id: string;
  scraper_id: string;
  scraper_name: string;
  scraper_destination_tenant: string;
  scraper_destination_category: string;
  scraper_domain: string | null;
  scraper_authority: string | null;
  scraper_jurisdiction: string | null;
  still_processing: boolean;
  processing: ScrapedDocumentChangeEvent[];
  created: ScrapedDocumentChangeEvent[];
  unchanged: ScrapedDocumentChangeEvent[];
  updated_content: ScrapedDocumentChangeEvent[];
  updated_url: ScrapedDocumentChangeEvent[];
  deleted: ScrapedDocumentChangeEvent[];
  fetch_errors: ScrapedDocumentChangeEvent[];
  processing_errors: ScrapedDocumentChangeEvent[];
  ingestion_errors: ScrapedDocumentChangeEvent[];
  ingesting: ScrapedDocumentChangeEvent[];
  completed: ScrapedDocumentChangeEvent[];
  ignored: ScrapedDocumentChangeEvent[];
  kept_document_external_ids: string[];
  pre_processing_duplicate_count: number;
  post_processing_duplicate_count: number;
}

export interface ScraperBacklogItem {
  scraper_id: string;
  name: string;
  destination_tenant_name: string;
  destination_tenant: string;
  destination_category: string;
  domain: string | null;
  authority: string | null;
  jurisdiction: string | null;
  pending_count: number;
  error_count: number;
  still_processing: boolean;
  total_document_count: number;
  last_run_finished_at: string | null;
}

export const getScrapersBacklog = async () => {
  const response = await apiGet('/admin/integrations/scraping/scrapers', {
    skipTenantPrefix: true,
    meta: { background: true, globalError: false }
  } as any);
  return response.data as ScraperBacklogItem[];
};

export const getScraperEvents = async (scraperId: string) => {
  const response = await apiGet(`/admin/integrations/scraping/scrapers/${scraperId}/events`, {
    skipTenantPrefix: true,
    meta: { background: true, globalError: false }
  } as any);
  return response.data as ScraperRunResults;
};

export const approveCreate = async (scraperId: string, scrapedUrlId: string) => {
  return apiPost(
    `/admin/integrations/scraping/scrapers/${scraperId}/actions/approve`,
    {
      scraped_url_id: scrapedUrlId
    },
    {
      skipTenantPrefix: true,
      meta: { background: true, globalError: false, title: 'Approve create' }
    } as any
  );
};

export const approveUpdate = async (scraperId: string, scrapedUrlId: string, oldDocumentExternalId: string) => {
  return apiPost(
    `/admin/integrations/scraping/scrapers/${scraperId}/actions/approve-update`,
    {
      scraped_url_id: scrapedUrlId,
      old_document_external_id: oldDocumentExternalId
    },
    {
      skipTenantPrefix: true,
      meta: { background: true, globalError: false, title: 'Approve update' }
    } as any
  );
};

export const approveUrlUpdate = async (scraperId: string, scrapedUrlId: string, documentExternalId: string) => {
  return apiPost(
    `/admin/integrations/scraping/scrapers/${scraperId}/actions/approve-url-update`,
    {
      scraped_url_id: scrapedUrlId,
      document_external_id: documentExternalId
    },
    {
      skipTenantPrefix: true,
      meta: { background: true, globalError: false, title: 'Approve URL update' }
    } as any
  );
};

export const ignoreUrl = async (scraperId: string, scrapedUrlId: string) => {
  return apiPost(
    `/admin/integrations/scraping/scrapers/${scraperId}/actions/ignore-url`,
    {
      scraped_url_id: scrapedUrlId
    },
    {
      skipTenantPrefix: true,
      meta: { background: true, globalError: false, title: 'Ignore URL' }
    } as any
  );
};

export const skipUpdate = async (scraperId: string, scrapedUrlId: string) => {
  return apiPost(
    `/admin/integrations/scraping/scrapers/${scraperId}/actions/skip`,
    {
      scraped_url_id: scrapedUrlId
    },
    {
      skipTenantPrefix: true,
      meta: { background: true, globalError: false, title: 'Skip update' }
    } as any
  );
};

export const retryScrapedUrl = async (scraperId: string, scrapedUrlId: string) => {
  return apiPost(
    `/admin/integrations/scraping/scrapers/${scraperId}/actions/retry`,
    {
      scraped_url_id: scrapedUrlId
    },
    {
      skipTenantPrefix: true,
      meta: { background: true, globalError: false, title: 'Retry' }
    } as any
  );
};

export const approveDelete = async (scraperId: string, documentExternalId: string) => {
  return apiPost(
    `/admin/integrations/scraping/scrapers/${scraperId}/actions/approve-delete`,
    {
      document_external_id: documentExternalId
    },
    {
      skipTenantPrefix: true,
      meta: { background: true, globalError: false, title: 'Approve delete' }
    } as any
  );
};

export const rejectDelete = async (scraperId: string, documentExternalId: string) => {
  return apiPost(
    `/admin/integrations/scraping/scrapers/${scraperId}/actions/reject-delete`,
    {
      document_external_id: documentExternalId
    },
    {
      skipTenantPrefix: true,
      meta: { background: true, globalError: false, title: 'Keep document' }
    } as any
  );
};

export const getFetchErrorBlobUrl = async (scrapedUrlId: string) => {
  const response = await apiGet(`/admin/integrations/scraping/scraped-urls/${scrapedUrlId}/fetch-error-blob`, {
    skipTenantPrefix: true,
    meta: { background: true, globalError: false }
  } as any);
  return response.data as { url: string };
};

export interface ScraperManagedDocument {
  document_id: string;
  external_id: string;
  title: string;
  url: string | null;
}

export const getScraperDocuments = async (scraperId: string) => {
  const response = await apiGet(`/admin/integrations/scraping/scrapers/${scraperId}/documents`, {
    skipTenantPrefix: true,
    meta: { background: true, globalError: false }
  } as any);
  return (response.data as Array<Partial<ScraperManagedDocument>>).map((doc) => ({
    document_id: String(doc.document_id ?? ''),
    external_id: String(doc.external_id ?? ''),
    title: doc.title || '',
    url: doc.url ?? null
  }));
};

export interface IgnoredUrlItem {
  id: string;
  final_url: string;
  sha256: string | null;
  created_at: string;
}

export const getIgnoredUrls = async (scraperId: string) => {
  const response = await apiGet(`/admin/integrations/scraping/scrapers/${scraperId}/ignored-urls`, {
    skipTenantPrefix: true,
    meta: { background: true, globalError: false }
  } as any);
  return response.data as IgnoredUrlItem[];
};

export const removeIgnoredUrl = async (scraperId: string, ignoredUrlId: string) => {
  return apiDelete(`/admin/integrations/scraping/scrapers/${scraperId}/ignored-urls/${ignoredUrlId}`, {
    skipTenantPrefix: true,
    meta: { background: true, globalError: false, title: 'Remove from ignore list' }
  } as any);
};

export const syncScraper = async (scraperId: string) => {
  const response = await apiPost(`/admin/integrations/scraping/scrapers/${scraperId}/sync`, null, {
    skipTenantPrefix: true,
    meta: { background: true, globalError: true, title: 'Trigger scraper sync' }
  } as any);
  return response.data;
};

export interface UpdateScraperMetadataPayload {
  domain?: string | null;
  authority?: string | null;
  jurisdiction?: string | null;
  propagate_null_jurisdiction?: boolean;
  propagate_null_authority?: boolean;
}

export const updateScraperMetadata = async (scraperId: string, metadata: UpdateScraperMetadataPayload) => {
  const response = await apiPatch(`/admin/integrations/scraping/scrapers/${scraperId}/metadata`, metadata, {
    skipTenantPrefix: true,
    meta: { background: false, globalError: true, title: 'Update scraper metadata' }
  } as any);
  return response.data;
};

// ─── Content diff / similarity ──────────────────────────────

export type DiffAiClassification = 'formatting_only' | 'minor_wording' | 'substantive_content_change' | 'unknown';

export interface ContentSimilarityItem {
  scraped_url_id: string;
  status: string | null;
  similarity_score: number | null;
  diff_url: string | null;
  error: string | null;
  ai_review_status: string | null;
  ai_classification: DiffAiClassification | null;
  ai_summary: string | null;
  ai_review_error: string | null;
}

export const getContentSimilarities = async (
  scraperId: string,
  pairs: Array<{ scraped_url_id: string; old_document_external_id: string }>
) => {
  const response = await apiPost(`/admin/integrations/scraping/scrapers/${scraperId}/content-similarities`, { pairs }, {
    skipTenantPrefix: true,
    meta: { background: true, globalError: false }
  } as any);
  return response.data as ContentSimilarityItem[];
};

// ─── Bulk accept/decline ─────────────────────────────────────

export interface BulkActionResultItem {
  scraped_url_id: string;
  success: boolean;
  error: string | null;
}

export const bulkSkipUpdates = async (scraperId: string, scrapedUrlIds: string[]) => {
  const response = await apiPost(
    `/admin/integrations/scraping/scrapers/${scraperId}/actions/bulk-skip`,
    { scraped_url_ids: scrapedUrlIds },
    {
      skipTenantPrefix: true,
      meta: { background: false, globalError: false, title: 'Bulk skip updates' }
    } as any
  );
  return response.data as BulkActionResultItem[];
};

export const bulkApproveUpdates = async (
  scraperId: string,
  items: Array<{ scraped_url_id: string; old_document_external_id: string }>
) => {
  const response = await apiPost(
    `/admin/integrations/scraping/scrapers/${scraperId}/actions/bulk-approve-update`,
    { items },
    {
      skipTenantPrefix: true,
      meta: { background: false, globalError: false, title: 'Bulk approve updates' }
    } as any
  );
  return response.data as BulkActionResultItem[];
};
